<template>
    <vx-modal :padding="false" v-model:visible="visible" size="md">
        <template v-slot:header>
            <div class="pt-6 px-6 pb-4">{{ isEditing ? 'Update' : 'New' }} Membership Status Definition</div>
        </template>

        <div class="p-6">
            <div class="mb-3">
                <vx-label>Name</vx-label>
                <vx-input
                    data-test="name-input"
                    v-model="formData.name"
                    size="lg"
                    :error="displayFirstError(validation.name)"
                ></vx-input>
            </div>
            <div class="mb-3">
                <vx-label>Classification Steering (optional)</vx-label>
                <vx-input
                    data-test="classification-steering-input"
                    v-model="formData.classification_steering"
                    size="lg"
                    :rows="4"
                    placeholder="Describe what effects on conversation category and section this membership status should have..."
                    :error="displayFirstError(validation.classification_steering)"
                ></vx-input>
            </div>
            <div class="mb-3">
                <vx-label>Message Steering (optional)</vx-label>
                <vx-input
                    data-test="message-steering-input"
                    v-model="formData.message_steering"
                    size="lg"
                    :rows="4"
                    placeholder="Describe how AI message generation should treat contacts with this membership status..."
                    :error="displayFirstError(validation.message_steering)"
                >
                    <template v-slot:hint> This steering guides AI message generation. </template>
                </vx-input>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 justify-between lg:flex-row">
                <vx-button color="muted" size="lg" :disabled="processing" @click="visible = false"> Cancel </vx-button>
                <vx-button
                    data-test="submit-btn"
                    class="grow"
                    color="primary"
                    size="lg"
                    type="button"
                    :loading="processing"
                    @click.prevent="submit()"
                >
                    {{ isEditing ? 'Update' : 'Create' }}
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { VxButton, VxInput, VxLabel, VxModal } from '@voxie/frontend-components';
import { required, maxLength } from '@vuelidate/validators';
import { computed, ref, watch } from 'vue';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { customAttributeValue, displayFirstError } from '~/utils/validation';
import type {
    MembershipStatusDefinitionFormData,
    MembershipStatusDefinitionType,
} from '~/services/membershipStatusDefinitions/membershipStatusDefinitionsClient.types';

const props = defineProps<{
    definition: MembershipStatusDefinitionType | null;
    editIndex: number | null;
}>();

const emit = defineEmits<{
    saved: [data: MembershipStatusDefinitionFormData, editIndex: number | null];
}>();

const visible = defineModel<boolean>('visible', { required: true });

const isEditing = computed(() => null !== props.editIndex);

const createEmptyFormData = (): MembershipStatusDefinitionFormData => ({
    name: '',
    classification_steering: null,
    message_steering: null,
});

const formData = ref<MembershipStatusDefinitionFormData>(createEmptyFormData());

watch(visible, (isVisible) => {
    if (isVisible) {
        if (props.definition) {
            formData.value = {
                name: props.definition.name,
                classification_steering: props.definition.classification_steering,
                message_steering: props.definition.message_steering,
            };
        } else {
            formData.value = createEmptyFormData();
        }
    } else {
        validation.value.$reset();
    }
});

const { submit, processing, validation } = useFormSubmission({
    data: formData,
    validation: {
        name: { required, maxLength: maxLength(40), customAttributeValue },
        classification_steering: { maxLength: maxLength(3000) },
        message_steering: { maxLength: maxLength(3000) },
    },
    submit: async () => {
        emit('saved', { ...formData.value }, props.editIndex);
    },
    preserveScroll: true,
});
</script>
